import React, { useState, useEffect } from "react";
import {
  getCatalogSettings,
  uploadCatalogFrontPage,
  uploadCatalogLastPage,
} from "../services/api";
import RetailerSidebar from "../components/retailer/RetailerSidebar";
import CatalogFirstPage from "../assets/catalog-front-page.jpg"
import CatalogLastPage from "../assets/catalog-last-page.jpeg"

export default function CatalogSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // First Page States
  const [firstPageEnabled, setFirstPageEnabled] = useState(true);
  const [selectedFirstPage, setSelectedFirstPage] = useState("default");
  const [firstPages, setFirstPages] = useState([
    {
      id: "default",
      name: "Default",
      image: CatalogFirstPage,
    },
  ]);
  const token = localStorage.getItem("retailer_token")

  // Last Page States
  const [lastPageEnabled, setLastPageEnabled] = useState(true);
  const [selectedLastPage, setSelectedLastPage] = useState("default");
  const [lastPages, setLastPages] = useState([
    {
      id: "default",
      name: "Default",
      image: CatalogLastPage,
    },
  ]);

  useEffect(() => {
  async function loadSettings() {
    try {
      setLoading(true);
      setError(null);
      
      const settings = await getCatalogSettings(token);

      if (settings) {
        // Base default option
        const defaultFront = {
          id: "default",
          name: "Default",
          image: CatalogFirstPage,
        };

        const defaultLast = {
          id: "default",
          name: "Default",
          image: CatalogLastPage,
        };

        // Reconstruct arrays directly instead of appending to prev state
        if (settings.front_page_url) {
          const fetchedFront = {
            id: "custom-front",
            name: "Uploaded Front Page",
            image: settings.front_page_url,
          };
          setFirstPages([defaultFront, fetchedFront]);
          setSelectedFirstPage("custom-front");
        } else {
          setFirstPages([defaultFront]);
        }

        if (settings.last_page_url) {
          const fetchedLast = {
            id: "custom-last",
            name: "Uploaded Last Page",
            image: settings.last_page_url,
          };
          setLastPages([defaultLast, fetchedLast]);
          setSelectedLastPage("custom-last");
        } else {
          setLastPages([defaultLast]);
        }

        // Sync Toggles
        if (typeof settings.enable_front_page === "boolean") {
          setFirstPageEnabled(settings.enable_front_page);
        }
        if (typeof settings.enable_last_page === "boolean") {
          setLastPageEnabled(settings.enable_last_page);
        }
      }
    } catch (err) {
      console.error("Error fetching catalog settings:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  loadSettings();
}, [token]);

  const handleUploadFirstPage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const token = localStorage.getItem("token");
      const response = await uploadCatalogFrontPage(file);
      const imageUrl = response.front_page_url || response.url || URL.createObjectURL(file);

      const newPage = {
        id: `front-${Date.now()}`,
        name: file.name,
        image: imageUrl,
      };

      setFirstPages((prev) => [...prev, newPage]);
      setSelectedFirstPage(newPage.id);
    } catch (err) {
      console.error("Upload front page error:", err);
      alert(err.message || "Failed to upload front page");
    }
  };

  const handleUploadLastPage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const token = localStorage.getItem("token");
      const response = await uploadCatalogLastPage(file, token);
      const imageUrl = response.last_page_url || response.url || URL.createObjectURL(file);

      const newPage = {
        id: `last-${Date.now()}`,
        name: file.name,
        image: imageUrl,
      };

      setLastPages((prev) => [...prev, newPage]);
      setSelectedLastPage(newPage.id);
    } catch (err) {
      console.error("Upload last page error:", err);
      alert(err.message || "Failed to upload last page");
    }
  };

  if (loading) {
    return (
      <div className="p-12 bg-slate-50 min-h-screen flex items-center justify-center text-gray-500 font-medium text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          Loading catalog settings...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
          <RetailerSidebar />
    
          {/* Main Content */}
          <main className="flex-1 ml-64 p-6 w-full">
    <div className="p-8 bg-slate-50 min-h-screen font-sans text-gray-800">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Catalog Settings</h1>
          <p className="text-sm text-gray-500 mt-1">
            Customize your First and Last pages, index, and product layouts.
          </p>
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
            {error}
          </div>
        )}

        {/* 1. First Page Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">First Page</h2>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 border border-indigo-600 text-indigo-600 hover:bg-indigo-50 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors">
                <span>+ Upload First Page</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUploadFirstPage}
                  className="hidden"
                />
              </label>

              <button
                type="button"
                onClick={() => setFirstPageEnabled(!firstPageEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                  firstPageEnabled ? "bg-indigo-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    firstPageEnabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {firstPages.map((item) => {
              const isSelected = selectedFirstPage === item.id;
              return (
                <div
                  key={item.id}
                  onClick={() => setSelectedFirstPage(item.id)}
                  className={`relative cursor-pointer rounded-lg border-2 overflow-hidden transition-all ${
                    isSelected
                      ? "border-indigo-600 shadow-md ring-1 ring-indigo-600"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="h-44 bg-gray-100 relative">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />

                    {isSelected && (
                      <div className="absolute top-2 right-2 bg-indigo-600 text-white rounded-full p-1 shadow">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="py-2.5 px-3 bg-white text-center border-t border-gray-100">
                    <span className="text-xs font-semibold text-gray-800 truncate block">
                      {item.name}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 2. Last Page Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Last Page</h2>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 border border-indigo-600 text-indigo-600 hover:bg-indigo-50 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors">
                <span>+ Upload Last Page</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUploadLastPage}
                  className="hidden"
                />
              </label>

              <button
                type="button"
                onClick={() => setLastPageEnabled(!lastPageEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                  lastPageEnabled ? "bg-indigo-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    lastPageEnabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {lastPages.map((item) => {
              const isSelected = selectedLastPage === item.id;
              return (
                <div
                  key={item.id}
                  onClick={() => setSelectedLastPage(item.id)}
                  className={`relative cursor-pointer rounded-lg border-2 overflow-hidden transition-all ${
                    isSelected
                      ? "border-indigo-600 shadow-md ring-1 ring-indigo-600"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="h-44 bg-gray-100 relative">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />

                    {isSelected && (
                      <div className="absolute top-2 right-2 bg-indigo-600 text-white rounded-full p-1 shadow">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="py-2.5 px-3 bg-white text-center border-t border-gray-100">
                    <span className="text-xs font-semibold text-gray-800 truncate block">
                      {item.name}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
    </main>
    </div>
  );
}